<template>
  <div
    class="w-full h-auto p-6 bg-white rounded-xl shadow-md border border-slate-200"
  >
    <h2 class="text-xl text-brand-700 font-semibold mb-6">RFI Analytics</h2>

    <div v-if="isLoading" class="text-center py-8">
      <p>Loading chart data...</p>
    </div>

    <div
      v-else
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 justify-items-center"
    >
      <div
        v-for="(chart, index) in charts"
        :key="index"
        class="w-full flex flex-col items-center bg-gray-50 rounded-xl shadow-md p-4"
      >
        <span class="text-center mb-4 text-lg font-medium text-brand-700">
          {{ chart.label }}
        </span>

        <div class="chart-container w-full h-80 bg-white rounded-lg shadow-sm">
          <component
            :is="chart.component"
            v-if="chart.data.labels?.length"
            :data="chart.data"
            :options="chart.options || chartOptions"
          />
          <p v-else class="text-gray-500 text-center py-8">
            No {{ chart.label.toLowerCase() }} data available
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, reactive } from "vue";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from "chart.js";
import {
  Doughnut as DoughnutChart,
  Bar as BarChart,
  Line as LineChart,
} from "vue-chartjs";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
);

export default {
  components: { DoughnutChart, BarChart, LineChart },
  props: {
    metrics: {
      type: Object,
      required: true,
      default: () => {},
    }
  },
  setup(props) {  

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
        },
        tooltip: {
          enabled: true,
        },
      },
    };

    // TEOPM brand palette for charts
    const BRAND_BLUE = "#2A4D80"; // brand-600
    const ACCENT_ORANGE = "#E8752E"; // accent-500
    const BRAND_CYCLE = [
      "#35619F",
      "#547EC2",
      "#213C64",
      "#7FA0D6",
      "#182B49",
    ];

    const cyclePalette = (count) =>
      Array.from({ length: count }, (_, i) => BRAND_CYCLE[i % BRAND_CYCLE.length]);

    // Manteniendo exactamente las mismas gráficas y estructuras de datos originales
    const completedNotComplete = computed(() => {
      return {
        labels: ["Completed", "Open"],
        datasets: [
          {
            backgroundColor: [BRAND_BLUE, ACCENT_ORANGE],
            data: [
              props.metrics.completedRfis || 0,
              props.metrics.openRfis || 0,
            ],
            borderWidth: 1,
          },
        ],
      };
    });

    const byRequester = computed(() => {
      return {
        labels: props.metrics.requestersMetrics.map((req) => req.name),
        datasets: [
          {
            label: "Open RFIs",
            backgroundColor: ACCENT_ORANGE,
            data: props.metrics.requestersMetrics.map((req) => req.open),
          },
          {
            label: "Completed RFIs",
            backgroundColor: BRAND_BLUE,
            data: props.metrics.requestersMetrics.map((req) => req.completed),
          },
        ],
      };
    });

    const byDirectedTo = computed(() => {
      const labels = Object.keys(props.metrics.directedToMetrics);
      return {
        labels,
        datasets: [
          {
            label: "",
            backgroundColor: cyclePalette(labels.length),
            data: Object.values(props.metrics.directedToMetrics).map(
              (item) => item.count
            ),
          },
        ],
      };
    });

    const byStatus = computed(() => {
      return {
        labels: Object.keys(props.metrics.statusCounts),
        datasets: [
          {
            label: "",
            borderColor: BRAND_BLUE,
            backgroundColor: "rgba(42, 77, 128, 0.15)",
            fill: true,
            data: Object.values(props.metrics.statusCounts).map((item) => item),
          },
        ],
      };
    });

    // Organizando las gráficas en el formato del primer componente
    const charts = reactive([
      {
        label: "Open vs Completed",
        component: DoughnutChart,
        data: completedNotComplete,
        options: chartOptions,
        type: "status",
      },
      {
        label: "By Requester",
        component: BarChart,
        data: byRequester,
        options: chartOptions,
        type: "requester",
      },
      {
        label: "Directed to",
        component: BarChart,
        data: byDirectedTo,
        options: chartOptions,
        type: "directed",
      },
      {
        label: "By Status",
        component: LineChart,
        data: byStatus,
        options: chartOptions,
        type: "status",
      },
    ]);

    return {
      charts,
      chartOptions,
    };
  },
};
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 300px;
  width: 100%;
}

.no-data-message {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #666;
  font-style: italic;
}
</style>